/**
 * Loads and saves one person's cellar against an ObjectStorage backend
 * (fake-storage.ts locally, s3-storage.ts for real — User Story 3), giving
 * the UI an always-current, optimistically-updated in-memory cellar.
 *
 * See specs/001-wine-cellar-tracker/plan.md "Change handling" and
 * contracts/storage-and-permissions.md "How the app reads and writes a
 * cellar" for the behaviour this implements:
 *   1. Every change is a pure `Cellar -> Cellar` function that also
 *      re-checks its own rules (it throws if invalid).
 *   2. The screen is updated immediately, synchronously, before any network
 *      call — `apply()` throws synchronously for an invalid change.
 *   3. The change is saved in the background with a conditional write.
 *      Further changes applied while a save is in flight are queued and
 *      combined into the *next* write, not a new one each.
 *   4. On a conflict (someone else saved first), the store reloads the
 *      latest document, re-applies the same pending change(s) in order,
 *      re-checking their rules against the fresh data, and retries a
 *      bounded number of times. A change that no longer holds is dropped;
 *      its caller is told why and the screen reverts to the fresh state.
 *   5. Any other save failure reverts to the last saved state; no silent
 *      retry.
 *   6. Undo is nothing special: the caller applies the inverse change.
 */
import type { Cellar, CellarChange } from '../domain/cellar';
import { createEmptyCellar } from '../domain/cellar';
import { migrateCellar } from '../domain/cellar-schema';
import { ConditionalWriteError, type ObjectStorage } from './fake-storage';

// CellarChange is a domain concept (domain/cellar.ts); re-exported here so
// existing callers of the store don't need a second import.
export type { CellarChange };

const MAX_CONFLICT_RETRIES = 3;

/** The cellar does not exist, and the store was told not to create it. */
export class CellarNotFoundError extends Error {
  constructor(public readonly cellarId: string) {
    super(`Cellar "${cellarId}" does not exist.`);
    this.name = 'CellarNotFoundError';
  }
}

export interface CellarStoreOptions {
  /**
   * Whether `load()` creates an empty cellar when there is none (default true). Only a person's
   * own cellar is created, at their first sign-in; the owner opening someone else's cellar
   * passes false, so a mistyped address never leaves a stray empty cellar (T073).
   */
  createIfMissing?: boolean;
}

interface PendingEntry {
  change: CellarChange;
  resolve: (cellar: Cellar) => void;
  reject: (error: unknown) => void;
}

export interface CellarStore {
  /**
   * Loads the cellar, creating an empty one on first use ("create only if absent") unless
   * `createIfMissing` is false, in which case a missing cellar rejects with CellarNotFoundError.
   */
  load(): Promise<Cellar>;
  /** The current, optimistically-updated cellar (safe to read synchronously). */
  getState(): Cellar;
  /**
   * Applies `change` to the in-memory cellar immediately. Throws synchronously
   * if `change` itself is invalid (e.g. stock would go negative right now).
   * The returned promise resolves once the change is durably saved, or
   * rejects (with the store reverted) if it ultimately could not be.
   */
  apply(change: CellarChange): Promise<Cellar>;
  /** Subscribe to every state change (optimistic apply, conflict replay, or revert). Returns an unsubscribe function. */
  onChange(listener: (cellar: Cellar) => void): () => void;
}

export function createCellarStore(
  storage: ObjectStorage,
  cellarId: string,
  options: CellarStoreOptions = {},
): CellarStore {
  const createIfMissing = options.createIfMissing ?? true;
  const path = `cellars/${cellarId}/cellar.json`;

  let base: { cellar: Cellar; etag: string } | undefined;
  let current: Cellar = createEmptyCellar();
  let pending: PendingEntry[] = [];
  let flushing = false;
  const listeners = new Set<(cellar: Cellar) => void>();

  function notify(): void {
    for (const listener of listeners) {
      listener(current);
    }
  }

  function revertAll(error: unknown): void {
    current = base!.cellar;
    for (const entry of pending) {
      entry.reject(error);
    }
    pending = [];
    notify();
  }

  async function flush(): Promise<void> {
    if (flushing) {
      return;
    }
    flushing = true;
    try {
      let conflictAttempts = 0;
      while (pending.length > 0) {
        const cellarSnapshot = current;
        // Snapshot exactly what we're about to send, then swap in a fresh
        // array so anything applied during the upcoming await is queued
        // separately (and gets combined into the *next* write instead).
        const pendingSnapshot = pending;
        pending = [];

        try {
          const result = await storage.put(path, cellarSnapshot, { ifMatch: base!.etag });
          base = { cellar: cellarSnapshot, etag: result.etag };
          for (const entry of pendingSnapshot) {
            entry.resolve(cellarSnapshot);
          }
          conflictAttempts = 0;
          continue;
        } catch (error) {
          if (!(error instanceof ConditionalWriteError)) {
            // Network/permission failure: revert everything (the snapshot we
            // failed to save, plus anything queued since), no silent retry.
            pending = [...pendingSnapshot, ...pending];
            revertAll(error);
            return;
          }

          conflictAttempts++;
          if (conflictAttempts > MAX_CONFLICT_RETRIES) {
            pending = [...pendingSnapshot, ...pending];
            revertAll(error);
            return;
          }

          const fresh = await readCellar();
          if (!fresh) {
            pending = [...pendingSnapshot, ...pending];
            revertAll(error);
            return;
          }

          // Replay every change we haven't yet confirmed saved, in order,
          // against the freshly reloaded document, re-checking each one's
          // own rules as it goes.
          const toReplay = [...pendingSnapshot, ...pending];
          let replayed = fresh.value;
          const stillPending: PendingEntry[] = [];
          let droppedFromHere = false;
          for (const entry of toReplay) {
            if (droppedFromHere) {
              entry.reject(
                new Error(
                  "Une modification précédente n'a pas pu être appliquée ; celle-ci a été annulée aussi.",
                ),
              );
              continue;
            }
            try {
              replayed = entry.change(replayed);
              stillPending.push(entry);
            } catch (ruleError) {
              entry.reject(ruleError);
              droppedFromHere = true;
            }
          }

          base = { cellar: fresh.value, etag: fresh.etag };
          current = replayed;
          pending = stillPending;
          notify();
          // loop again: try saving the replayed state
        }
      }
    } finally {
      flushing = false;
    }
  }

  /** The stored document brought up to the current version (CLAUDE.md data safety rule 3). */
  async function readCellar(): Promise<{ value: Cellar; etag: string } | undefined> {
    const stored = await storage.get<unknown>(path);
    return stored && { value: migrateCellar(stored.value), etag: stored.etag };
  }

  return {
    async load(): Promise<Cellar> {
      const existing = await readCellar();
      if (existing) {
        base = { cellar: existing.value, etag: existing.etag };
      } else if (!createIfMissing) {
        throw new CellarNotFoundError(cellarId);
      } else {
        const empty = createEmptyCellar();
        try {
          const created = await storage.put(path, empty, { ifNoneMatch: '*' });
          base = { cellar: empty, etag: created.etag };
        } catch (error) {
          if (!(error instanceof ConditionalWriteError)) {
            throw error;
          }
          // Someone else created it between our get() and put(): just load theirs.
          const raceWinner = await readCellar();
          if (!raceWinner) {
            throw error;
          }
          base = { cellar: raceWinner.value, etag: raceWinner.etag };
        }
      }
      current = base.cellar;
      pending = [];
      return current;
    },

    getState(): Cellar {
      return current;
    },

    apply(change: CellarChange): Promise<Cellar> {
      if (!base) {
        throw new Error('CellarStore.apply() called before load()');
      }
      // Runs synchronously: throws immediately for an invalid change, with no state mutation.
      current = change(current);
      notify();

      return new Promise<Cellar>((resolve, reject) => {
        pending.push({ change, resolve, reject });
        void flush();
      });
    },

    onChange(listener: (cellar: Cellar) => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
